import { PARAGRAPH } from "@packages/design-system/constants";
import { cn } from "@packages/lib/utils";
import React from "react";
import { titleCase } from "title-case";

// Utility to anchorize heading text
function anchorize(text: string): string {
  return text
    .toLowerCase()
    .replace(/[^\w\s-]/g, "") // remove non-word chars
    .trim()
    .replace(/\s+/g, "-"); // replace spaces with hyphens
}

interface HeadingProps {
  level: 1 | 2 | 3 | 4 | 5;
  className?: string;
  id?: string;
  background?: "tertiary" | "default";
  heading: string;
  subheading?: string;
  headingSize?: 1 | 2 | 3 | 4 | 5;
  hasTopPadding?: boolean;
  isTextCentered?: boolean;
  hasNoMarginBottom?: boolean;
  description?: React.ReactNode;
}

// Typography plugin base (16px) heading sizes, margins, and line-heights
const headingSizes = {
  1: "text-4xl/9",
  2: "text-3xl/8",
  3: "text-2xl/7",
  4: "text-xl/7",
  5: "text-lg/6",
};

const headingTopMargin = {
  1: "mt-0",
  2: "mt-12",
  3: "mt-8",
  4: "mt-6",
  5: "mt-4",
};

const headingBottomMargin = {
  1: "mb-8",
  2: "mb-3",
  3: "mb-2.5",
  4: "mb-2",
  5: "mb-2",
};

// Map for subheading sizes, fallback to text-sm for smallest
const subheadingSizes = {
  1: "text-xl/7",
  2: "text-lg/6",
  3: "text-base/6",
  4: "text-base/6",
  5: "text-base/6",
};

// Map for subheading margin below headings
const subheadingMargins = {
  1: "mt-2",
  2: "mt-1.5",
  3: "mt-1",
  4: "mt-1",
  5: "mt-1",
};

const Heading: React.FC<HeadingProps> = ({
  level,
  className,
  id,
  background = "default",
  heading,
  subheading,
  headingSize,
  hasTopPadding = false,
  isTextCentered = false,
  hasNoMarginBottom = false,
  description,
}) => {
  const Tag: React.ElementType = `h${level}`;
  // Determine the base size (headingSize or level)
  let baseSize = headingSize ?? level;
  // Calculate subheading size: two sizes smaller, but not smaller than 5
  if (baseSize > 5) baseSize = 5;
  const subheadingClass =
    subheadingSizes[baseSize as keyof typeof subheadingSizes] || "text-sm";

  const topMargin = hasTopPadding
    ? headingTopMargin[baseSize as keyof typeof headingTopMargin] || "mt-0"
    : "mt-0";
  const bottomMargin = hasNoMarginBottom
    ? ""
    : headingBottomMargin[baseSize as keyof typeof headingBottomMargin] || "";
  const containerMargin = cn(
    topMargin,
    bottomMargin,
    isTextCentered && "text-center",
  );
  const computedId = id ?? anchorize(heading);

  let renderedDescription: React.ReactNode = description;
  if (typeof description === "string") {
    if (description.includes("\n\n")) {
      renderedDescription = description.split("\n\n").map((paragraph, idx) => (
        <p key={idx} className={PARAGRAPH}>
          {paragraph}
        </p>
      ));
    } else {
      renderedDescription = <p className={PARAGRAPH}>{description}</p>;
    }
  }
  return (
    <div className={containerMargin}>
      <Tag
        id={computedId}
        className={cn(
          "scroll-mt-4 font-semibold tracking-tight text-balance lg:scroll-mt-24",
          background === "tertiary"
            ? "text-tertiary-foreground"
            : "text-heading",
          headingSizes[baseSize],
          className,
        )}
        aria-level={level}
      >
        {titleCase(heading)}
      </Tag>
      {subheading && (
        <p
          className={cn(
            "font-medium text-pretty",
            background === "tertiary"
              ? "text-tertiary-foreground"
              : "text-muted-heading",
            subheadingClass,
            subheadingMargins[baseSize as keyof typeof subheadingMargins] || "",
          )}
        >
          {subheading}
        </p>
      )}
      {renderedDescription && (
        <div
          className={cn(
            "mt-1.5",
            background === "tertiary"
              ? "text-tertiary-foreground"
              : "text-foreground",
          )}
        >
          {renderedDescription}
        </div>
      )}
    </div>
  );
};

export default Heading;
